/**
 * VersionUpdateDialog — surfaces a "new version available" modal when the
 * deployed bundle has been updated since this tab loaded.
 *
 * Pattern adapted from trnznd-website's VersionUpdateDialog. trnznd uses a
 * Next.js /api/version route that returns the current commit SHA; here we
 * publish a static dist/build-id.json at build time (via buildIdPlugin in
 * vite.config.ts) and fetch it with cache: no-store.
 *
 * Comparison: __BUILD_ID__ (baked into the bundle by Vite `define`) vs
 * the server's current buildId. When they diverge, the user sees a modal
 * with Dismiss / Refresh. Dismiss hides for the rest of this tab session;
 * Refresh hard-reloads to pick up the new bundle.
 *
 * Polling: every 60s plus on tab visibility change (catches the common
 * case where a user leaves a tab open for hours and comes back to a stale
 * build). Local "dev-*" builds are skipped — version-check is for shipped
 * deployments only.
 */

import { useEffect, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { safeStorage } from '@/lib/safeStorage';

const POLL_MS = 60_000;
/** When the user dismisses the modal, suppress it for 1 hour app-wide
 *  (key persists in localStorage so multiple tabs honour the same grace).
 *  Per Ian: don't nag. If they refused once, give them an hour. */
const DISMISS_GRACE_MS = 60 * 60 * 1000;
const DISMISS_KEY = 'tb_version_dismissed_until';
/** Max time to wait for a new SW to take control after we trigger update().
 *  If it doesn't activate in this window we just reload anyway — the
 *  next page load will pick up the new SW via the regular registration. */
const SW_TAKEOVER_TIMEOUT_MS = 3000;

function isWithinDismissGrace(): boolean {
  const raw = safeStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const until = parseInt(raw, 10);
  if (!Number.isFinite(until)) return false;
  if (Date.now() < until) return true;
  // Grace expired — clean up the stale key.
  safeStorage.remove(DISMISS_KEY);
  return false;
}

export function VersionUpdateDialog() {
  const { t } = useLanguage();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    // Skip for local dev builds — the dev-<ts> ID is unique per build but
    // there's no production version to drift from.
    if (__BUILD_ID__.startsWith('dev-')) return;
    if (dismissed) return;

    let cancelled = false;

    async function check() {
      // Honour the 1-hour dismiss grace — no popup, no nag.
      if (isWithinDismissGrace()) return;
      try {
        const res = await fetch('/build-id.json', { cache: 'no-store' });
        if (!res.ok) return;
        const { buildId } = (await res.json()) as { buildId?: string };
        if (cancelled) return;
        if (
          buildId &&
          !buildId.startsWith('dev-') &&
          buildId !== __BUILD_ID__
        ) {
          setUpdateAvailable(true);
        }
      } catch {
        // Network error / offline — silent. Will retry on next interval.
      }
    }

    check();
    const interval = setInterval(check, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [dismissed]);

  useEffect(() => {
    if (!updateAvailable) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDismissed(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [updateAvailable]);

  /** Two-step refresh:
   *  1. Force the SW registration to re-check sw.js (in case the
   *     polling-the-build-id detection ran before the SW realised there was
   *     a new version waiting).
   *  2. If a new SW is installing/waiting, ask it to take over and wait
   *     for controllerchange (max SW_TAKEOVER_TIMEOUT_MS).
   *  3. Reload — guaranteed to be served by the new SW.
   *  Falls back to plain reload if anything fails. */
  async function handleRefresh() {
    if (refreshing) return;
    setRefreshing(true);
    // Drop any dismiss grace — the user explicitly chose to refresh.
    safeStorage.remove(DISMISS_KEY);

    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          await reg.update();
          if (reg.waiting) {
            reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
          if (reg.installing || reg.waiting) {
            await new Promise<void>((resolve) => {
              const timeout = setTimeout(resolve, SW_TAKEOVER_TIMEOUT_MS);
              navigator.serviceWorker.addEventListener(
                'controllerchange',
                () => {
                  clearTimeout(timeout);
                  resolve();
                },
                { once: true },
              );
            });
          }
        }
      }
    } catch (err) {
      console.warn('[VersionUpdateDialog] SW takeover failed; reloading anyway', err);
    }
    window.location.reload();
  }

  function handleDismiss() {
    safeStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_GRACE_MS));
    setDismissed(true);
  }

  if (!updateAvailable || dismissed) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleDismiss}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-[var(--text-primary)] mb-2">
          {t.common.updateAvailableTitle || 'New version available'}
        </h3>
        <p className="text-sm text-[var(--text-secondary)] mb-5 whitespace-pre-line">
          {t.common.updateAvailableBody ||
            'Refresh to load the latest version of TripBoom. Your unsaved changes may be lost.'}
        </p>
        <div className="flex gap-3">
          <button
            onClick={handleDismiss}
            disabled={refreshing}
            className="flex-1 py-2 rounded-xl border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--accent-teal)]/40 transition-colors text-sm disabled:opacity-50"
          >
            {t.toasts?.dismiss || 'Dismiss'}
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex-1 py-2 rounded-xl font-semibold transition-colors text-sm bg-gradient-to-r from-[#2dd4bf] to-[#f59e0b] text-black hover:shadow-lg disabled:opacity-70"
          >
            {refreshing ? '…' : (t.common.refresh || 'Refresh')}
          </button>
        </div>
      </div>
    </div>
  );
}
